import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getRoleHubRelations } from "../utils/cookieUtils";

export const ROLES = {
  SUPER_ADMIN: 1,
  ADMIN: 2,
  USER: 3,
  SUPPORT: 4,
  FINANCE: 5,
  MANAGER: 6,
  HUB_MANAGER: 7,
  HUB_CLUSTER_MANAGER: 10,
  SUPPORT_ADMIN: 11,
} as const;

const ALL_PAGES = [
  "/dashboard",
  "/user-management",
  "/staff-management",
  "/hub-management",
  "/vehicle-management",
  "/booking-management",
  "/ride-management",
  "/payment-management",
  "/refund-management",
  "/plan-management",
  "/support",
  "/marketing/promo-codes",
  "/marketing/promo-codes/create",
  "/marketing/referral-settings",
  "/settings",
  "/user/profile",
] as const;

const PAGE_ACCESS: Record<number, readonly string[]> = {
  [ROLES.SUPER_ADMIN]: ALL_PAGES,
  [ROLES.ADMIN]: [
    "/dashboard",
    "/user-management",
    "/staff-management",
    "/hub-management",
    "/vehicle-management",
    "/booking-management",
    "/ride-management",
    "/payment-management",
    "/refund-management",
    "/support",
    "/marketing/promo-codes",
    "/marketing/promo-codes/create",
    "/marketing/referral-settings",
    "/settings",
    "/user/profile",
  ],
  [ROLES.HUB_MANAGER]: [
    "/dashboard",
    "/user-management",
    "/staff-management",
    "/hub-management",
    "/vehicle-management",
    "/booking-management",
    "/ride-management",
    "/payment-management",
    "/refund-management",
    "/support",
    "/marketing/promo-codes",
    "/marketing/promo-codes/create",
    "/marketing/referral-settings",
    "/user/profile",
  ],
  [ROLES.HUB_CLUSTER_MANAGER]: [
    "/dashboard",
    "/user-management",
    "/staff-management",
    "/hub-management",
    "/vehicle-management",
    "/booking-management",
    "/ride-management",
    "/payment-management",
    "/refund-management",
    "/support",
    "/marketing/promo-codes",
    "/marketing/promo-codes/create",
    "/marketing/referral-settings",
    "/user/profile",
  ],
  [ROLES.SUPPORT]: [
    "/booking-management",
    "/ride-management",
    "/support",
    "/user/profile",
  ],
  [ROLES.SUPPORT_ADMIN]: [
    "/dashboard",
    "/user-management",
    "/staff-management",
    "/hub-management",
    "/vehicle-management",
    "/booking-management",
    "/ride-management",
    "/payment-management",
    "/refund-management",
    "/support",
    "/marketing/promo-codes",
    "/marketing/promo-codes/create",
    "/marketing/referral-settings",
    "/user/profile",
  ],
  [ROLES.FINANCE]: [
    "/dashboard",
    "/plan-management",
    "/payment-management",
    "/refund-management",
    "/ride-management",
    "/user/profile",
  ],
  [ROLES.MANAGER]: ["/dashboard", "/ride-management", "/user/profile"],
};

// Feature access mapping
const FEATURE_ACCESS: Record<
  number,
  {
    canCreate: boolean;
    canExport: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canViewAll: boolean;
  }
> = {
  [ROLES.SUPER_ADMIN]: {
    canCreate: true,
    canExport: true,
    canEdit: true,
    canDelete: true,
    canViewAll: true,
  },
  [ROLES.ADMIN]: {
    canCreate: true,
    canExport: true,
    canEdit: true,
    canDelete: false,
    canViewAll: true,
  },
  [ROLES.HUB_MANAGER]: {
    canCreate: true,
    canExport: true,
    canEdit: true,
    canDelete: false,
    canViewAll: false,
  },
  [ROLES.HUB_CLUSTER_MANAGER]: {
    canCreate: true,
    canExport: true,
    canEdit: true,
    canDelete: false,
    canViewAll: true,
  },
  [ROLES.SUPPORT]: {
    canCreate: true,
    canExport: true,
    canEdit: true,
    canDelete: false,
    canViewAll: false,
  },
  [ROLES.SUPPORT_ADMIN]: {
    canCreate: true,
    canExport: true,
    canEdit: true,
    canDelete: false,
    canViewAll: true,
  },
  [ROLES.FINANCE]: {
    canCreate: true,
    canExport: true,
    canEdit: true,
    canDelete: false,
    canViewAll: true,
  },
  [ROLES.MANAGER]: {
    canCreate: false,
    canExport: false,
    canEdit: false,
    canDelete: false,
    canViewAll: false,
  },
};

// Role names as used in API/dropdown (must match GetAllRoles response)
export const ROLE_NAMES = {
  SUPER_ADMIN: "SuperAdmin",
  ADMIN: "Admin",
  SUPPORT: "Support",
  FINANCE: "Finance",
  HUB_MANAGER: "Hub Manager",
  HUB_CLUSTER_MANAGER: "Hub Cluster Manager",
  SUPPORT_ADMIN: "Support Admin",
  USER: "User",
  MANAGER: "Manager",
} as const;

/**
 * Hierarchy: who can create which roles when creating staff.
 * Super Admin: all staff roles.
 * Admin: Support, Finance, Hub Manager, Hub Cluster Manager, Support Admin.
 * Hub Manager: Support, Finance.
 * Hub Cluster Manager: Support, Finance, Hub Manager.
 * Support Admin: Support.
 * Support / Finance / Manager: none.
 */
const CREATABLE_STAFF_ROLES_BY_ROLE_ID: Record<number, readonly string[]> = {
  [ROLES.SUPER_ADMIN]: [
    ROLE_NAMES.ADMIN,
    ROLE_NAMES.SUPPORT,
    ROLE_NAMES.FINANCE,
    ROLE_NAMES.HUB_MANAGER,
    ROLE_NAMES.HUB_CLUSTER_MANAGER,
    ROLE_NAMES.SUPPORT_ADMIN,
    ROLE_NAMES.MANAGER,
  ],
  [ROLES.ADMIN]: [
    ROLE_NAMES.SUPPORT,
    ROLE_NAMES.FINANCE,
    ROLE_NAMES.HUB_MANAGER,
    ROLE_NAMES.HUB_CLUSTER_MANAGER,
    ROLE_NAMES.SUPPORT_ADMIN,
  ],
  [ROLES.SUPPORT]: [],
  [ROLES.FINANCE]: [],
  [ROLES.MANAGER]: [],
  [ROLES.HUB_MANAGER]: [ROLE_NAMES.SUPPORT, ROLE_NAMES.FINANCE],
  [ROLES.HUB_CLUSTER_MANAGER]: [
    ROLE_NAMES.SUPPORT,
    ROLE_NAMES.FINANCE,
    ROLE_NAMES.HUB_MANAGER,
  ],
  [ROLES.SUPPORT_ADMIN]: [ROLE_NAMES.SUPPORT],
};

// Effective role IDs from role-hub relations (or single user.roleid when no relations)
const getEffectiveRoleIds = (
  userRoleId: number | undefined,
  relations: { roleid: number }[] | null,
): number[] => {
  if (!userRoleId) return [];
  if (relations && relations.length > 0) {
    const roleIds = Array.from(new Set(relations.map((r) => r.roleid)));
    return roleIds.length > 0 ? roleIds : [userRoleId];
  }
  return [userRoleId];
};

export const useRBAC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const relations = getRoleHubRelations();

  const effectiveRoleIds = getEffectiveRoleIds(user?.roleid, relations);

  const hasRole = (roleId: number): boolean => {
    return effectiveRoleIds.includes(roleId);
  };

  const hasAnyRole = (roleIds: number[]): boolean => {
    return effectiveRoleIds.some((rid) => roleIds.includes(rid));
  };

  const canAccessPage = (path: string): boolean => {
    if (effectiveRoleIds.length === 0) return false;

    for (const roleId of effectiveRoleIds) {
      const allowedPages = PAGE_ACCESS[roleId];
      if (
        allowedPages?.some(
          (allowedPath) =>
            path === allowedPath || path.startsWith(allowedPath + "/"),
        )
      ) {
        return true;
      }
    }
    return false;
  };

  const canAccessFeature = (
    feature: keyof (typeof FEATURE_ACCESS)[typeof ROLES.SUPER_ADMIN],
  ): boolean => {
    if (effectiveRoleIds.length === 0) return false;
    for (const roleId of effectiveRoleIds) {
      const roleFeatures = FEATURE_ACCESS[roleId];
      if (roleFeatures?.[feature]) return true;
    }
    return false;
  };

  /** Role names the current user is allowed to assign when creating/editing staff (hierarchy-based). */
  const getCreatableRoleNames = (): string[] => {
    if (effectiveRoleIds.length === 0) return [];
    const set = new Set<string>();
    for (const roleId of effectiveRoleIds) {
      const list = CREATABLE_STAFF_ROLES_BY_ROLE_ID[roleId];
      if (list) list.forEach((name) => set.add(name));
    }
    return Array.from(set);
  };

  const getRoleName = (): string => {
    return user?.rolename || "Unknown";
  };

  const getRoleId = (): number | null => {
    return user?.roleid || null;
  };

  const isSuperAdmin = (): boolean => {
    return hasRole(ROLES.SUPER_ADMIN);
  };

  const isHubManager = (): boolean => {
    return hasRole(ROLES.HUB_MANAGER);
  };

  const isHubClusterManager = (): boolean => {
    return hasRole(ROLES.HUB_CLUSTER_MANAGER);
  };

  const isSupport = (): boolean => {
    return hasRole(ROLES.SUPPORT);
  };

  const isFinance = (): boolean => {
    return hasRole(ROLES.FINANCE);
  };

  const isManager = (): boolean => {
    return hasRole(ROLES.MANAGER);
  };

  return {
    // Role checks
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isHubManager,
    isHubClusterManager,
    isSupport,
    isFinance,
    isManager,

    // Access checks
    canAccessPage,
    canAccessFeature,

    // Getters
    getRoleName,
    getRoleId,
    getCreatableRoleNames,

    // Feature permissions
    canCreate: () => canAccessFeature("canCreate"),
    canExport: () => canAccessFeature("canExport"),
    canEdit: () => canAccessFeature("canEdit"),
    canDelete: () => canAccessFeature("canDelete"),
    canViewAll: () => canAccessFeature("canViewAll"),
  };
};

export default useRBAC;
